/**
 * 3D Room Visualizer — Garage Door Builder
 *
 * Creates realistic garage door meshes that overlay on wall surfaces.
 * Doors are placed in front of wall materials and visible from both sides.
 *
 * Door design: Classic raised-panel garage door with 4 horizontal sections
 * and rectangular panel insets per section.
 */

import * as THREE from 'three';
import { WallFace } from './types';

// Z-offset to place doors in front of wall boards (feet)
const DOOR_OFFSET = 0.04;

// Door panel colors
const DOOR_BODY_COLOR = 0x8c8c8c;       // Medium gray body
const DOOR_PANEL_COLOR = 0xa0a0a0;       // Lighter panel insets
const DOOR_FRAME_COLOR = 0x555555;       // Dark frame/mullions
const DOOR_HANDLE_COLOR = 0x333333;      // Handle

// ============================================================================
// PUBLIC API
// ============================================================================

export interface GarageDoorConfig {
  enabled: boolean;
  width: number;    // Width per door in feet
  height: number;   // Door height in feet
  numDoors: number; // Number of doors
}

/**
 * Create garage door meshes for a wall in the full-room 3D viewer.
 * Returns a THREE.Group to add to the wall's parent group.
 *
 * @param config - Garage door configuration
 * @param wallLength - Total wall length in feet
 * @param face - Which wall face (for 3D positioning)
 * @param roomLength - Room length for coordinate transforms
 * @param roomWidth - Room width for coordinate transforms
 */
export function createGarageDoorGroup(
  config: GarageDoorConfig,
  wallLength: number,
  face: WallFace,
  roomLength: number,
  roomWidth: number
): THREE.Group {
  const group = new THREE.Group();
  if (!config.enabled || config.numDoors < 1) return group;

  const { width: doorW, height: doorH, numDoors } = config;

  // Calculate horizontal positions using justify-content-around spacing:
  // Equal space on all sides including edges: totalSpace / (numDoors + 1) * i
  // But "around" means each item gets equal space on both sides.
  // space-around: each door gets wallLength/numDoors space, centered within it.
  const positions = calculateDoorPositions(wallLength, doorW, numDoors);

  for (const xCenter of positions) {
    const doorGroup = buildSingleDoor(doorW, doorH);
    // Position door: x = center, y = doorH/2 (flush to bottom), z = in front
    positionDoorOnWall(doorGroup, xCenter, doorH / 2, face, roomLength, roomWidth);
    group.add(doorGroup);
  }

  return group;
}

/**
 * Create garage door meshes for the single-wall 3D viewer.
 * Wall is on the Z=0 plane, facing +Z. No coordinate transform needed.
 */
export function createGarageDoorGroupForWall(
  config: GarageDoorConfig,
  wallLength: number
): THREE.Group {
  const group = new THREE.Group();
  if (!config.enabled || config.numDoors < 1) return group;

  const { width: doorW, height: doorH, numDoors } = config;
  const positions = calculateDoorPositions(wallLength, doorW, numDoors);

  // Place doors on BOTH sides of the wall (+Z front and -Z back)
  for (const zSign of [1, -1]) {
    for (const xCenter of positions) {
      const doorGroup = buildSingleDoor(doorW, doorH);
      doorGroup.position.set(xCenter, doorH / 2, DOOR_OFFSET * zSign);
      if (zSign < 0) doorGroup.rotation.y = Math.PI;
      group.add(doorGroup);
    }
  }

  return group;
}

// ============================================================================
// POSITIONING
// ============================================================================

/**
 * Calculate horizontal center positions for doors using justify-content-around logic.
 * Each door gets equal surrounding space: segment = wallLength / numDoors,
 * door centered within each segment.
 */
function calculateDoorPositions(wallLength: number, doorWidth: number, numDoors: number): number[] {
  if (numDoors === 1) {
    return [wallLength / 2];
  }

  // justify-content-around: divide wall into numDoors equal segments
  const segment = wallLength / numDoors;
  const positions: number[] = [];
  for (let i = 0; i < numDoors; i++) {
    positions.push(segment * i + segment / 2);
  }
  return positions;
}

/**
 * Position and orient a door group on a specific wall face in room coordinates.
 */
function positionDoorOnWall(
  doorGroup: THREE.Group,
  xOnWall: number,
  yCenter: number,
  face: WallFace,
  roomLength: number,
  roomWidth: number
): void {
  switch (face) {
    case 'front':
      doorGroup.position.set(xOnWall, yCenter, DOOR_OFFSET);
      break;
    case 'back':
      doorGroup.position.set(roomLength - xOnWall, yCenter, roomWidth - DOOR_OFFSET);
      doorGroup.rotation.y = Math.PI;
      break;
    case 'left':
      doorGroup.position.set(DOOR_OFFSET, yCenter, xOnWall);
      doorGroup.rotation.y = Math.PI / 2;
      break;
    case 'right':
      doorGroup.position.set(roomLength - DOOR_OFFSET, yCenter, roomWidth - xOnWall);
      doorGroup.rotation.y = -Math.PI / 2;
      break;
  }
}

// ============================================================================
// DOOR GEOMETRY BUILDING
// ============================================================================

/**
 * Build a single garage door centered at origin.
 * The door faces +Z and extends from -w/2 to +w/2 horizontally
 * and -h/2 to +h/2 vertically.
 *
 * Design: Classic raised-panel garage door with:
 * - Outer frame
 * - 4 horizontal sections (separated by horizontal mullions)
 * - 2-3 rectangular raised panels per section
 * - A small handle on the bottom section
 */
function buildSingleDoor(doorWidth: number, doorHeight: number): THREE.Group {
  const group = new THREE.Group();
  const hw = doorWidth / 2;
  const hh = doorHeight / 2;

  // Materials (double-sided so visible from both sides of wall)
  const bodyMat = new THREE.MeshStandardMaterial({
    color: 0x000000,
    emissive: DOOR_BODY_COLOR,
    emissiveIntensity: 0.85,
    roughness: 0.7,
    metalness: 0.1,
    side: THREE.DoubleSide,
  });

  const panelMat = new THREE.MeshStandardMaterial({
    color: 0x000000,
    emissive: DOOR_PANEL_COLOR,
    emissiveIntensity: 0.85,
    roughness: 0.6,
    metalness: 0.05,
    side: THREE.DoubleSide,
  });

  const frameMat = new THREE.MeshStandardMaterial({
    color: 0x000000,
    emissive: DOOR_FRAME_COLOR,
    emissiveIntensity: 0.85,
    roughness: 0.8,
    metalness: 0.2,
    side: THREE.DoubleSide,
  });

  // Build door details on BOTH sides (+Z front and -Z back) so the door
  // looks correct from inside and outside the room.
  const SIDES = [1, -1]; // +Z (inside face) and -Z (outside face)

  for (const zSign of SIDES) {
    // 1) Main door body (background plane)
    const bodyGeo = new THREE.PlaneGeometry(doorWidth, doorHeight);
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.position.z = 0;
    if (zSign < 0) body.rotation.y = Math.PI; // flip to face -Z
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);

    // 2) Outer frame lines
    const frameLines = createFrameLines(hw, hh, zSign);
    group.add(frameLines);

    // 3) Horizontal sections & panels
    const FRAME_INSET = Math.min(doorWidth * 0.03, 0.15);
    const MULLION_HEIGHT = Math.min(doorHeight * 0.015, 0.06);
    const NUM_SECTIONS = 4;
    const usableHeight = doorHeight - 2 * FRAME_INSET;
    const sectionHeight = (usableHeight - (NUM_SECTIONS - 1) * MULLION_HEIGHT) / NUM_SECTIONS;

    const panelsPerSection = doorWidth >= 14 ? 4 : doorWidth >= 9 ? 3 : 2;
    const PANEL_GAP = Math.min(doorWidth * 0.02, 0.12);
    const PANEL_INSET = Math.min(doorWidth * 0.04, 0.2);

    for (let s = 0; s < NUM_SECTIONS; s++) {
      const sectionBottom = -hh + FRAME_INSET + s * (sectionHeight + MULLION_HEIGHT);
      const sectionCenterY = sectionBottom + sectionHeight / 2;

      // Horizontal mullion line (between sections)
      if (s > 0) {
        const mullionY = sectionBottom - MULLION_HEIGHT / 2;
        const mullionGeo = new THREE.PlaneGeometry(doorWidth - 2 * FRAME_INSET, MULLION_HEIGHT);
        const mullion = new THREE.Mesh(mullionGeo, frameMat);
        mullion.position.set(0, mullionY, 0.002 * zSign);
        group.add(mullion);
      }

      // Raised panels within this section
      const panelAreaWidth = doorWidth - 2 * FRAME_INSET - 2 * PANEL_INSET;
      const panelWidth = (panelAreaWidth - (panelsPerSection - 1) * PANEL_GAP) / panelsPerSection;
      const panelHeight = sectionHeight * 0.7;

      for (let p = 0; p < panelsPerSection; p++) {
        const panelX = -hw + FRAME_INSET + PANEL_INSET + p * (panelWidth + PANEL_GAP) + panelWidth / 2;
        const panelGeo = new THREE.PlaneGeometry(panelWidth, panelHeight);
        const panel = new THREE.Mesh(panelGeo, panelMat);
        panel.position.set(panelX, sectionCenterY, 0.003 * zSign);
        group.add(panel);

        // Panel border (raised look)
        const panelBorder = createPanelBorder(panelX, sectionCenterY, panelWidth, panelHeight, zSign);
        group.add(panelBorder);
      }
    }

    // 4) Handle on bottom-center
    const handleY = -hh + FRAME_INSET + sectionHeight * 0.35;
    const handleMat = new THREE.MeshStandardMaterial({
      color: 0x000000,
      emissive: DOOR_HANDLE_COLOR,
      emissiveIntensity: 0.85,
      roughness: 0.4,
      metalness: 0.5,
      side: THREE.DoubleSide,
    });
    const handleGeo = new THREE.PlaneGeometry(doorWidth * 0.08, doorHeight * 0.01);
    const handle = new THREE.Mesh(handleGeo, handleMat);
    handle.position.set(0, handleY, 0.005 * zSign);
    group.add(handle);
  }

  return group;
}

/**
 * Create the outer frame lines for the door.
 */
function createFrameLines(hw: number, hh: number, zSign: number = 1): THREE.LineSegments {
  const points: number[] = [];
  const z = 0.001 * zSign;

  // Outer rectangle
  points.push(-hw, -hh, z, hw, -hh, z);
  points.push(hw, -hh, z, hw, hh, z);
  points.push(hw, hh, z, -hw, hh, z);
  points.push(-hw, hh, z, -hw, -hh, z);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));

  return new THREE.LineSegments(geo, new THREE.LineBasicMaterial({
    color: DOOR_FRAME_COLOR,
    linewidth: 2,
  }));
}

/**
 * Create a subtle border around a raised panel for the 3D raised look.
 */
function createPanelBorder(cx: number, cy: number, w: number, h: number, zSign: number = 1): THREE.LineSegments {
  const hw = w / 2;
  const hh = h / 2;
  const z = 0.004 * zSign;
  const points: number[] = [];

  points.push(cx - hw, cy - hh, z, cx + hw, cy - hh, z);
  points.push(cx + hw, cy - hh, z, cx + hw, cy + hh, z);
  points.push(cx + hw, cy + hh, z, cx - hw, cy + hh, z);
  points.push(cx - hw, cy + hh, z, cx - hw, cy - hh, z);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));

  return new THREE.LineSegments(geo, new THREE.LineBasicMaterial({
    color: DOOR_FRAME_COLOR,
    linewidth: 1,
    transparent: true,
    opacity: 0.6,
  }));
}
